import cloneDeep from "lodash.clonedeep";
import { actions } from "./actions";
import { DEFAULT_STATE } from "./state";
import { mutationTypes, getterTypes } from "./types";

// API responses (e.g. after adding a user) may omit right_groups — templates rely on it being an array
const normalizeUser = (u) => {
    const user = { ...u };
    if (user.last_login) {
        user.last_login = new Date(user.last_login);
    }
    if (user.last_invitation) {
        user.last_invitation = new Date(user.last_invitation);
    }
    if (!Array.isArray(user.right_groups)) {
        user.right_groups = [];
    }
    return user;
};

export default {
    actions,
    mutations: {
        [mutationTypes.usersGetDataStart](state) {
            state.isLoading = true;
            state.data = null;
        },
        [mutationTypes.usersGetDataSuccess](state, data) {
            state.isLoading = false;
            state.data = data.map(normalizeUser);
        },
        [mutationTypes.replaceUserInStore](state, { user, replaceData }) {
            const userIdx = state.data.findIndex((i) => i.id === user.id);
            if (userIdx >= 0) {
                state.data[userIdx] = { ...state.data[userIdx], ...replaceData };
            }
        },
        [mutationTypes.usersGetDataFailure](state) {
            state.isLoading = false;
        },
        [mutationTypes.usersGetAllDataStart](state) {
            state.isLoading = true;
            state.data = null;
        },
        [mutationTypes.usersGetAllDataSuccess](state, response) {
            state.isLoading = false;
            const users = [];
            const data = response.data || response;

            if (Array.isArray(data)) {
                // If the response is an array (without pagination)
                data.forEach((u) => {
                    users.push(normalizeUser(u));
                });
                state.data = users;
            } else {
                // If the response is an object with pagination
                (data.items || data.users || []).forEach((u) => {
                    users.push(normalizeUser(u));
                });
                state.data = users;

                // Update pagination info
                if (data.pagination) {
                    const totalItems = data.pagination.total_items || data.pagination.totalItems || users.length;
                    const itemsPerPage = data.pagination.items_per_page || data.pagination.itemsPerPage || 20;

                    state.pagination = {
                        currentPage: data.pagination.current_page || data.pagination.currentPage || 1,
                        totalPages: Math.ceil(totalItems / itemsPerPage),
                        totalItems: totalItems,
                        itemsPerPage: itemsPerPage,
                    };
                }
            }
        },
        [mutationTypes.usersGetAllDataFailure](state) {
            state.isLoading = false;
        },
        [mutationTypes.userAddStart](state) {
            state.userFormErrors = null;
            state.isLoading = true;
        },
        [mutationTypes.userAddSuccess](state, data) {
            state.isLoading = false;
            state.data = [...state.data, normalizeUser(data)];
        },
        [mutationTypes.userAddFailure](state) {
            state.isLoading = false;
        },
        [mutationTypes.userEditStart](state) {
            state.userFormErrors = null;
            state.isLoading = true;
        },
        [mutationTypes.userEditSuccess](state, data) {
            state.isLoading = false;
            state.data = state.data.map((item) => (item.id === data.id ? normalizeUser(data) : item));
        },
        [mutationTypes.userEditFailure](state) {
            state.isLoading = false;
        },
        [mutationTypes.userAddEditErrors](state, errors) {
            state.userFormErrors = errors;
        },
        [mutationTypes.userDeleteStart](state) {
            state.isLoading = true;
        },
        [mutationTypes.userDeleteSuccess](state, data) {
            state.isLoading = false;
            state.data = state.data.filter((item) => item.id !== data.id);
        },
        [mutationTypes.userDeleteFailure](state) {
            state.isLoading = false;
        },
        [mutationTypes.userInviteStart]() {},
        [mutationTypes.userInviteSuccess]() {},
        [mutationTypes.userInviteFailure]() {},
        [mutationTypes.removeRightGroupStart](state) {
            state.isLoading = true;
        },
        [mutationTypes.removeRightGroupSuccess](state, data) {
            state.isLoading = false;

            // If the API returns users with updated right_groups, use that
            if (data.user && data.user.id) {
                const userIndex = state.data.findIndex((user) => user.id == data.user.id);
                if (userIndex >= 0) {
                    state.data[userIndex] = { ...state.data[userIndex], ...data.user };
                }
            } else {
                // Fallback: remove right_group by ID (for older APIs)
                const rightGroupId = data.right_group_id || data.id;
                state.data.forEach((user) => {
                    if (user.right_groups && Array.isArray(user.right_groups)) {
                        user.right_groups = user.right_groups.filter((group) => group.id !== rightGroupId);
                    }
                });
            }
        },
        [mutationTypes.removeRightGroupFailure](state) {
            state.isLoading = false;
        },
        [mutationTypes.addRightGroupStart](state) {
            state.isLoading = true;
        },
        [mutationTypes.addRightGroupSuccess](state, data) {
            state.isLoading = false;

            // The API returns a complete user with updated right_groups
            if (data.user && data.user.id) {
                const userIndex = state.data.findIndex((user) => user.id == data.user.id);
                if (userIndex >= 0) {
                    // Replace users with updated right_groups
                    state.data[userIndex] = { ...state.data[userIndex], ...data.user };
                }
            }
        },
        [mutationTypes.addRightGroupFailure](state) {
            state.isLoading = false;
        },
    },
    getters: {
        [getterTypes.getUsers](state) {
            return state.data;
        },
        [getterTypes.getUserById](state) {
            return (userId) => {
                return state.data.find((i) => i.id === userId);
            };
        },
        [getterTypes.getUserFormErrors](state) {
            return state.userFormErrors;
        },
        [getterTypes.getPagination](state) {
            return state.pagination;
        },
    },
    state: cloneDeep(DEFAULT_STATE),
};
